// Dropdown that lists active outreachEmailTemplate rows and calls
// onPick(template) when the user selects one. Lazily loads on first
// render; the dropdown shows a small empty-state if there are none yet
// (with a link to /admin/email-templates/new).

import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import client from '../lib/feathers';

export default function TemplatePicker({ onPick }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open || templates.length > 0 || loading) return;
    setLoading(true);
    client.service('admin/outreach-email-template').find({ query: { $limit: 200 } })
      .then((r) => setTemplates(r.data || []))
      .catch(() => setTemplates([]))
      .finally(() => setLoading(false));
  }, [open, templates.length, loading]);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  return (
    <div ref={wrapRef} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="px-3 py-1.5 text-sm border border-gray-300 rounded-md bg-white hover:bg-gray-50 text-gray-700"
      >
        Start from template ▾
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-80 bg-white shadow-lg rounded-md border border-gray-200 py-1 max-h-96 overflow-auto">
          {loading && <div className="px-3 py-2 text-sm text-gray-500">Loading…</div>}
          {!loading && templates.length === 0 && (
            <div className="px-3 py-4 text-sm text-gray-500">
              <p className="mb-2">No templates yet.</p>
              <Link to="/admin/email-templates/new" className="text-blue-700 hover:underline">
                Create one →
              </Link>
            </div>
          )}
          {!loading && templates.length > 0 && (
            <>
              {templates.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => { onPick?.(t); setOpen(false); }}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                >
                  <div className="font-medium text-gray-900">{t.name}</div>
                  {t.description && (
                    <div className="text-xs text-gray-500 mt-0.5">{t.description}</div>
                  )}
                  <div className="text-xs text-gray-400 mt-0.5 truncate">{t.subject}</div>
                </button>
              ))}
              <div className="border-t border-gray-100 px-3 py-2 text-xs">
                <Link to="/admin/email-templates" className="text-blue-700 hover:underline">
                  Manage templates →
                </Link>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
